<template>
  <b-card
      no-body
      class="vb-card-recap"
  >
    <b-card-header class="vb-card-recap__header text text--base text-primary semibold">
      <div class="vb-card-recap__header-item">
        <vb-icon
            :name="'calendar'"
            :size="20"
            :color="sass('primary')"
            class="mr-1"
        />
        {{ dates_label }}
      </div>
      <div class="vb-card-recap__header-item">
        <vb-icon
          :name="'occupants'"
          :size="20"
          :color="sass('primary')"
          class="mr-1"
        />
        {{ pax_label }}
        <span
          v-for="label in extra_pax_labels"
          :key="label"
          class="ml-1"
        >+ {{ label }}</span>
      </div>
      <div 
        v-if="has_flight"
        class="vb-card-recap__header-item"
      >
        <span class="d-flex align-items-center">
          <vb-icon
            :name="'airplane'"
            :size="20"
            :color="sass('primary')"
            class="mr-1"
          />
          da&nbsp;<span class="text-capitalize">{{ recap.departure_airport.toLowerCase() }}</span>&nbsp;({{ recap.departure_airport_code }})
        </span>
        &nbsp;
        <span>
          a&nbsp;<span class="text-capitalize">{{ recap.arrival_airport.toLowerCase() }}</span>&nbsp;({{ recap.arrival_airport_code }})
        </span>
      </div>
    </b-card-header>
    <b-card-body>
      <b-card-title class="vb-card-recap__title">
        <span class="vb-card-recap__title-item regular">
          <vb-icon
              :name="'bed'"
              :size="17"
              :color="sass('primary')"
              class="mr-1"
          />{{ recap.room_name.toLowerCase() }}
        </span>
        <span class="vb-card-recap__title-item regular">
          <vb-icon
              :name="'cutlery'"
              :size="17"
              :color="sass('primary')"
              class="mr-1"
          />{{ recap.board_name.toLowerCase() }}
        </span> 
        <span v-if="has_flight" class="vb-card-recap__title-item regular">
          <vb-icon
              :name="'bus'"
              :size="17"
              :color="sass('primary')"
              class="mr-1"
          />Trasferimenti inclusi <span class="text-primary">*</span>
        </span>
      </b-card-title>
      <b-card-text class="vb-card-recap__note">
        <span>
          Il prezzo che vedi è già comprensivo delle riduzioni o eventuali gratuità previste per i bambini.
        </span>
        <span v-if="has_flight">
          <strong class="vb-card-recap__note-transfers">*I trasferimenti potrebbero non essere sempre inclusi.</strong>
          Per maggiori dettagli, verifica le informazioni presenti nel pacchetto scelto o contatta la nostra assistenza.
        </span>
      </b-card-text>
      <div class="vb-card-recap__footer">
        <insufficient-credit-alert
          :availability="availability"
          :total_price="display_price"
          class_name="vb-card-recap__nocredit"
          icon_class_name="vb-card-recap__nocredit-icon ml-1"
          cta_id="alpitour-card-recap-insufficient-credit"
        />
        <span
          class="vb-card-recap__amount text-primary"
          :class="is_over_credit ? 'text-dark' : ''"
        >
          € {{ display_price }}
          <em class="vb-card-recap__amount-perpax">
            (€ {{ price_per_person }}/pers.)
          </em>
        </span>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { parse_date } from '@utilities/commons' 
import { round, divide } from 'mathjs'
import InsufficientCreditAlert from '@alpitour/components/atoms/insufficient-credit-alert.vue'

const pluralize = (count, singular, plural) =>
  `${count} ${count > 1 ? plural : singular}`

const format_day = (date) => {
  const { day_number = '', month_string = '', year = '' } = parse_date(date) || {}
  return `${day_number} ${month_string} ${year}`
}

export default {
  name: 'card-recap',
  components: {
    'insufficient-credit-alert': InsufficientCreditAlert,
  },
  props: {
    recap: {
      type: Object,
      default: () => undefined,
    }
  },
  computed: {
    availability() {
      return this.get_availability()
    },
    has_flight() {
      return Boolean(this.recap.departure_airport)
    },
    total_pax() {
      return this.recap.adults + this.recap.children + this.recap.infants
    },
    dates_label() {
      const { checkin, checkout, nights } = this.recap
      return `${format_day(checkin)} - ${format_day(checkout)} (${pluralize(nights, 'notte', 'notti')})`
    },
    pax_label() {
      return pluralize(this.recap.adults, 'adulto', 'adulti')
    },
    extra_pax_labels() {
      const { children, infants } = this.recap
      const labels = []
      if (children > 0) labels.push(pluralize(children, 'bambino', 'bambini'))
      if (infants > 0) labels.push(pluralize(infants, 'infante', 'infanti'))
      return labels
    },
    display_price() {
      return this.markup(this.recap.total_amount).display_price
    },
    price_per_person() {
      if (!this.total_pax) return 0
      return round(divide(this.display_price, this.total_pax), 2)
    },
    is_over_credit() {
      const { is_known, total } = this.availability
      return is_known && total < this.display_price
    },
  },
}
</script>

<style lang="scss" scoped>

%vb-card-recap__item {
  display: flex;
  align-items: center;
  flex-basis: 100%;
  margin-bottom: .625rem;

  &:last-child {
    margin-bottom: 0;
  }

  @include media-breakpoint-up(md) {
    flex-basis: auto;
    margin-right: 15px;

    &:last-child {
      margin-right: 0;
    }
  }

  @include media-breakpoint-up(xl) {
    margin-bottom: 0;
  }
}

.vb-card-recap {
  margin-top: 0 !important;
  margin-bottom: $grid-gutter-width;

  &__header {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    background-color: transparent;

    &:before {
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border-radius: inherit;
      background-color: var(--primary);
      opacity: .03;
      pointer-events: none;
    }

    &-item {
      @extend %vb-card-recap__item;

      @at-root .card-header & {
        &:last-child {
          display: block;
          
          @include media-breakpoint-up(sm) {
            display: flex;
          }
        }
      }
    }
  }

  &__title {
    text-transform: capitalize;
    display: flex;
    flex-wrap: wrap;

    &-item {
      font-size: $font-size-base;
      line-height: 2rem;
      @extend %vb-card-recap__item;
    }
  }

  &__note {

    span {
      display: block;
    }

    &-transfers {
      color: var(--primary);
      font-weight: $vb-font-weight-semibold;
    }
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: flex-end;
    gap: $grid-gutter-width/2;
    margin-top: $grid-gutter-width/2;
    padding-top: $grid-gutter-width/2;
    border-top: 1px solid $gray-200;
  }

  &__amount {
    font-size: $font-size-lg;
    font-weight: $vb-font-weight-semibold;

    &-perpax {
      font-size: $font-size-sm;
      font-weight: $font-weight-normal;
      color: $gray-600;
    }
  }

  // la classe viaggia come prop fino a un figlio di insufficient-credit-alert,
  // che non riceve l'attributo di scope: senza ::v-deep la regola non aggancia
  ::v-deep .vb-card-recap__nocredit {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    font-weight: $vb-font-weight-semibold;
    font-size: $font-size-md;
  }
}

</style>
